import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  setDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import {
  FinancialPlan,
  FinancialPlanData,
  FinancialPlanSummary,
} from '../types/financialPlan';
import { mergeFinancialPlanData } from './financialPlanDefaults';
import { removeUndefinedDeep } from './removeUndefinedDeep';

function plansCollection(uid: string) {
  return collection(db, 'users', uid, 'financialPlans');
}

function planDoc(uid: string, planId: string) {
  return doc(db, 'users', uid, 'financialPlans', planId);
}

function dataFromFirestore(data: Record<string, unknown>): FinancialPlanData {
  const raw = data as Partial<FinancialPlanData>;
  return mergeFinancialPlanData(raw, { preserveStored: true });
}

function planFromDoc(id: string, data: Record<string, unknown>): FinancialPlan {
  const createdAt = (data.createdAt as Timestamp)?.toDate?.() ?? new Date();
  const updatedAt = (data.updatedAt as Timestamp)?.toDate?.() ?? new Date();
  return {
    id,
    name: typeof data.name === 'string' ? data.name : 'Untitled Plan',
    createdAt,
    updatedAt,
    ...dataFromFirestore(data),
  };
}

export async function listFinancialPlans(uid: string): Promise<FinancialPlanSummary[]> {
  const q = query(plansCollection(uid), orderBy('updatedAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => {
    const data = d.data();
    return {
      id: d.id,
      name: typeof data.name === 'string' ? data.name : 'Untitled Plan',
      updatedAt: (data.updatedAt as Timestamp)?.toDate?.() ?? new Date(),
    };
  });
}

export async function getFinancialPlan(uid: string, planId: string): Promise<FinancialPlan | null> {
  const snap = await getDoc(planDoc(uid, planId));
  if (!snap.exists()) return null;
  return planFromDoc(snap.id, snap.data());
}

export async function saveFinancialPlan(
  uid: string,
  planId: string | null,
  name: string,
  data: FinancialPlanData,
): Promise<string> {
  const now = Timestamp.now();
  const id = planId ?? doc(plansCollection(uid)).id;
  const ref = planDoc(uid, id);
  const existing = planId ? await getDoc(ref) : null;
  const createdAt = existing?.exists()
    ? (existing.data()?.createdAt as Timestamp) ?? now
    : now;

  const payload = removeUndefinedDeep({
    name,
    ...mergeFinancialPlanData(data, { preserveStored: true }),
    createdAt,
    updatedAt: now,
  });

  await setDoc(ref, payload);

  return id;
}

export async function deleteFinancialPlan(uid: string, planId: string): Promise<void> {
  await deleteDoc(planDoc(uid, planId));
}
